import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useNavigate } from "@tanstack/react-router";
import { HelpCircle, Keyboard, LogOut, Palette, Settings, User } from "lucide-react";
import { useSession } from "../react/providers/SessionProvider";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { initialsOf } from "./workspace-utils";
import { ThemeDialog } from "./ThemeDialog";

// User menu: identity + entries. Theme opens a dialog (not an inline switcher).
export function UserPreferenceManager() {
    const session = useSession();
    const runtime = useRuntime();
    const navigate = useNavigate();
    const [themeOpen, setThemeOpen] = useState(false);

    const onLogout = async () => {
        try {
            await runtime.services.auth.logout();
        } finally {
            navigate({ to: "/auth/login" } as Parameters<typeof navigate>[0]);
        }
    };

    return (
        <>
            <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                    <button className="user-switcher" aria-label={`User menu — ${session.name}`} title={session.name}>
                        <span className="user-switcher__tile">{initialsOf(session.name)}</span>
                    </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content className="rdx-menu user-menu" align="end" sideOffset={6}>
                        <div className="user-menu__header">
                            <span className="avatar avatar--sq avatar--lg">{initialsOf(session.name)}</span>
                            <div className="user-menu__id">
                                <div className="user-menu__name">{session.name}</div>
                                {session.email ? <div className="user-menu__email">{session.email}</div> : null}
                            </div>
                        </div>

                        <DropdownMenu.Separator className="rdx-menu__sep" />

                        <DropdownMenu.Item
                            className="rdx-menu__item"
                            onSelect={(e) => {
                                e.preventDefault();
                                requestAnimationFrame(() => setThemeOpen(true));
                            }}
                        >
                            <Palette size={14} className="rdx-menu__item-icon" />
                            <span className="rdx-menu__item-primary">Theme</span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item className="rdx-menu__item">
                            <User size={14} className="rdx-menu__item-icon" />
                            <span className="rdx-menu__item-primary">Profile &amp; Preferences</span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item className="rdx-menu__item">
                            <Settings size={14} className="rdx-menu__item-icon" />
                            <span className="rdx-menu__item-primary">Account Settings</span>
                        </DropdownMenu.Item>

                        <DropdownMenu.Separator className="rdx-menu__sep" />

                        <DropdownMenu.Item className="rdx-menu__item">
                            <Keyboard size={14} className="rdx-menu__item-icon" />
                            <span className="rdx-menu__item-primary">Shortcuts</span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item className="rdx-menu__item">
                            <HelpCircle size={14} className="rdx-menu__item-icon" />
                            <span className="rdx-menu__item-primary">Help &amp; Support</span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item className="rdx-menu__item" onSelect={() => void onLogout()}>
                            <LogOut size={14} className="rdx-menu__item-icon" />
                            <span className="rdx-menu__item-primary">Log out</span>
                        </DropdownMenu.Item>
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>

            <ThemeDialog open={themeOpen} onOpenChange={setThemeOpen} />
        </>
    );
}
